import { getMyTenantPreferences, updateMyTenantPreferences } from '@kudos/http-client'
import { useApiError } from '@shared/errors/useApiError'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useInvalidateQuery } from './useInvalidateQuery'

// Must match the MS Teams admin page so both share the GET /identity/tenantPreferences/my cache.
export const IDENTITY_TENANT_PREFERENCES_MY_QK = 'identityTenantPreferencesMy'

type UseTenantGifsEnabledOptions = {
  /**
   * Lets a caller with no GIF affordance skip the request entirely, so an editor
   * whose call site never offers GIFs doesn't fetch a preference it can't act on.
   */
  enabled?: boolean
}

/**
 * Read-only view of the tenant's GIF setting, for the features that have to hide
 * their GIF affordances. Falls back to enabled so a failed request leaves
 * today's behaviour intact; wait for `isLoading` before rendering an affordance
 * that would have to be taken away again.
 */
export const useTenantGifsEnabled = ({ enabled = true }: UseTenantGifsEnabledOptions = {}) => {
  const { data, isLoading } = useQuery({
    queryKey: [IDENTITY_TENANT_PREFERENCES_MY_QK],
    queryFn: getMyTenantPreferences,
    enabled,
    // Every composer on a page reads this, so don't refetch on each mount. The
    // admin mutation below invalidates the key, so a save is still reflected.
    staleTime: 5 * 60 * 1000,
  })

  return {
    gifsEnabled: data?.gifsEnabled ?? true,
    // AND with `enabled` so a disabled observer does not inherit isLoading
    // from a sibling that is fetching the same cache entry. React Query's
    // isLoading is `isPending && isFetching` on the shared query, so a
    // comment composer in flight would otherwise stall award/announcement
    // editors that opted out.
    isLoading: enabled && isLoading,
  }
}

const useTenantGifPreference = () => {
  const invalidateQuery = useInvalidateQuery()
  const { handleApiError } = useApiError()

  const { gifsEnabled, isLoading } = useTenantGifsEnabled()

  const { mutateAsync: updateGifsEnabled, isPending } = useMutation({
    mutationFn: (enabled: boolean) => updateMyTenantPreferences({ gifsEnabled: enabled }),
    onSuccess: async () => {
      await invalidateQuery([IDENTITY_TENANT_PREFERENCES_MY_QK])
    },
    onError: error => handleApiError(error),
  })

  return {
    gifsEnabled,
    updateGifsEnabled,
    isPending,
    isLoading,
  }
}

export default useTenantGifPreference
